import {
  FileText,
  Loader2,
  RotateCcw,
  Upload,
  UploadCloud,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import type {
  ReadTemplate,
  ReadTemplateVersion,
} from '@/services/api/generated/templatesV2Service';
import { useTemplateVersionHistoryQuery } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateRestoreVersionMutation,
  useTemplateUploadNewVersionMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

import { TEMPLATE_STATUS_COLORS, TEMPLATE_STATUS_LABELS } from './constants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACCEPT_MAP: Record<string, Record<string, string[]>> = {
  PDF: { 'application/pdf': ['.pdf'] },
  DOCX: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
  },
  HTML: { 'text/html': ['.html', '.htm'] },
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TemplateVersionHistoryProps {
  versionGroup: string;
  currentVersion: number;
  templateId: string;
  templateFormat: string;
  templateScope: string;
  uploadExpanded: boolean;
  setUploadExpanded: (expanded: boolean) => void;
  onVersionCreated: (newTemplateId: string) => void;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function formatVersionDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const LoadingSkeleton = () => {
  return (
    <div className="space-y-4 p-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-700" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 w-20 animate-pulse rounded bg-zinc-200 dark:bg-zinc-700" />
            <div className="h-3 w-28 animate-pulse rounded bg-zinc-200 dark:bg-zinc-700" />
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateVersionHistory = ({
  versionGroup,
  currentVersion,
  templateId,
  templateFormat,
  templateScope,
  uploadExpanded,
  setUploadExpanded,
  onVersionCreated,
}: TemplateVersionHistoryProps) => {
  const { data, isLoading, error } = useTemplateVersionHistoryQuery({
    versionGroup,
  });
  const [restoreVersion, { isLoading: isRestoring }] =
    useTemplateRestoreVersionMutation();
  const [uploadNewVersion] = useTemplateUploadNewVersionMutation();

  // ---- Upload state ----
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showGlobalWarning, setShowGlobalWarning] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ phase: string; message: string; detail?: string }[]>([]);

  // Generated type is singular ReadTemplateVersion but the list endpoint returns ReadTemplateVersion[].
  // Cast required until the OpenAPI spec is corrected (see v2-cutover-manifest.md).
  const versions = (data as unknown as ReadTemplateVersion[] | undefined) ?? [];

  // Sort descending by version number (most recent first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  // ---- Upload handler (validates before creating) ----
  const handleUpload = useCallback(async () => {
    if (!uploadFile || !templateId) return;
    setIsUploading(true);
    try {
      // Step 1: Validate file before creating new version
      const validateFormData = new FormData();
      validateFormData.append('file', uploadFile);
      const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] ?? '';
      const backendHost = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string;

      try {
        const validateResponse = await fetch(
          `${backendHost}/api/templates/validate/`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-CSRFToken': csrfToken },
            body: validateFormData,
          },
        );
        if (validateResponse.ok) {
          const validation = await validateResponse.json();
          if (!validation.valid && validation.errors?.length > 0) {
            setValidationErrors(validation.errors);
            setShowValidationErrors(true);
            return;
          }
        }
      } catch {
        // Validation endpoint failed — proceed anyway
      }

      // Step 2: Upload (file is valid)
      const result: ReadTemplate = await uploadNewVersion({
        templateId,
        file: uploadFile,
      }).unwrap();
      toast.success(`Version ${result.version} uploaded`);
      setUploadFile(null);
      setUploadExpanded(false);
      onVersionCreated(result.id);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload new version',
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    uploadFile,
    templateId,
    uploadNewVersion,
    setUploadExpanded,
    onVersionCreated,
  ]);

  const handleUploadClick = useCallback(() => {
    if (templateScope === 'GLOBAL') {
      setShowGlobalWarning(true);
    } else {
      handleUpload();
    }
  }, [templateScope, handleUpload]);

  const handleRestore = useCallback(
    async (version: number) => {
      if (!templateId) return;
      try {
        const result = await restoreVersion({ templateId, version }).unwrap();
        toast.success(`Restored to v${version}`);
        onVersionCreated(result.id);
      } catch {
        toast.error('Failed to restore version');
      }
    },
    [templateId, restoreVersion, onVersionCreated],
  );

  const dropzone = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length > 0) setUploadFile(accepted[0]);
    },
    accept: ACCEPT_MAP[templateFormat] ?? {},
    multiple: false,
    disabled: isUploading,
  });

  const isExternal = templateFormat === 'EXTERNAL';

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="p-4 text-sm font-medium text-red-600">
        Failed to load version history.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload New Version button + dropzone */}
      {!isExternal && (
        <div className="space-y-3 px-1">
          <Button
            outline
            className="w-full"
            onClick={() => {
              setUploadExpanded(!uploadExpanded);
              setUploadFile(null);
            }}
          >
            <Upload className="h-4 w-4" data-slot="icon" />
            {uploadExpanded ? 'Cancel Upload' : 'Upload New Version'}
          </Button>

          {uploadExpanded && (
            <div>
              {!uploadFile ? (
                <div
                  {...dropzone.getRootProps()}
                  className={cn(
                    'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed py-6 transition-colors',
                    dropzone.isDragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                      : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50 dark:border-zinc-600 dark:hover:border-zinc-500 dark:hover:bg-zinc-800/50',
                  )}
                >
                  <input {...dropzone.getInputProps()} />
                  <UploadCloud
                    className={cn(
                      'mb-2 h-6 w-6',
                      dropzone.isDragActive
                        ? 'text-blue-500'
                        : 'text-zinc-500',
                    )}
                  />
                  <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    {dropzone.isDragActive
                      ? 'Drop file here'
                      : 'Drop or click to browse'}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-zinc-500">
                    Must be a {templateFormat} file
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/50">
                  <FileText className="h-4 w-4 flex-shrink-0 text-zinc-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {uploadFile.name}
                    </p>
                    <p className="text-xs font-medium text-zinc-500">
                      {formatFileSize(uploadFile.size)}
                    </p>
                  </div>
                  {!isUploading && (
                    <button
                      type="button"
                      onClick={() => setUploadFile(null)}
                      className="rounded-md p-0.5 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <Button
                    color="blue"
                    className="ml-1 h-7 px-2.5 text-xs"
                    onClick={handleUploadClick}
                    disabled={isUploading}
                  >
                    {isUploading && (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    )}
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Global scope warning dialog */}
      <AlertDialog open={showGlobalWarning} onOpenChange={setShowGlobalWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Global Template Warning</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;re about to upload a new version of a globally scoped
              template. This could impact all firms. Are you sure you want to
              do this?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleUpload();
              }}
            >
              Yes, Upload Global Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Validation Error Modal */}
      <AlertDialog open={showValidationErrors} onOpenChange={setShowValidationErrors}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Template is Malformed
            </AlertDialogTitle>
            <AlertDialogDescription>
              The file has issues that need to be fixed before uploading.
              No new version was created.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2 max-h-[300px] overflow-y-auto">
            {validationErrors.map((err, idx) => (
              <div key={idx} className="rounded-lg border border-red-200 bg-red-50 p-3">
                <span className="text-[9px] font-black uppercase tracking-wider text-red-400">
                  {err.phase}
                </span>
                <p className="text-sm font-semibold text-red-900 mt-0.5">{err.message}</p>
                {err.detail && (
                  <p className="text-xs font-medium text-red-700 mt-1 whitespace-pre-wrap">{err.detail}</p>
                )}
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Timeline */}
      {sortedVersions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-sm font-medium text-zinc-500">
            No version history available
          </p>
        </div>
      ) : (
        <div className="relative pl-6">
          {/* Timeline line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-zinc-200 dark:bg-zinc-700" />

          <div className="space-y-4">
            {sortedVersions.map((version) => {
              const isCurrent = version.version === currentVersion;
              const statusLabel =
                TEMPLATE_STATUS_LABELS[version.status] ?? version.status;
              const statusColor =
                TEMPLATE_STATUS_COLORS[version.status] ??
                'bg-zinc-100 text-zinc-600';

              return (
                <div key={version.id} className="group/version relative flex items-start gap-3">
                  {/* Timeline dot */}
                  <div
                    className={cn(
                      'absolute -left-6 top-1 h-2.5 w-2.5 rounded-full border-2',
                      isCurrent
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-900',
                    )}
                  />

                  {/* Content */}
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                          v{version.version}
                        </span>
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            statusColor,
                          )}
                        >
                          {statusLabel}
                        </span>
                        {isCurrent && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs font-medium text-zinc-500">
                        {formatVersionDate(version.created_at)}
                      </p>
                    </div>
                    {!isCurrent && (
                      <button
                        type="button"
                        disabled={isRestoring}
                        onClick={() => handleRestore(version.version)}
                        className="flex-shrink-0 rounded-md p-1 text-zinc-500 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 group-hover/version:opacity-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                        title={`Restore v${version.version}`}
                      >
                        {isRestoring ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
